import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import toast from 'react-hot-toast'
import Modal from '../../Unknown/Modal'
import ConfirmActionOtpModal from '../ConfirmActionOtpModal'
import CustomPhoneInput from '../../UI/PhoneInput'
import translate from '../../../i18n/translate'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import {
  smsAdminApi,
  OrganizerSmsAudience,
} from '../../../modules/services/smsAdminService'

const AUDIENCES: OrganizerSmsAudience[] = [
  'allOrganizers',
  'activeOrganizers',
  'notActiveOrganizers',
]

function extractErrorMessage(res: any): string | null {
  const data = res?.data
  if (data?.message?.message) return String(data.message.message)
  if (typeof data?.message === 'string') return data.message
  const errData = res?.error?.data
  if (errData?.message?.message) return String(errData.message.message)
  if (typeof errData?.message === 'string') return errData.message
  return null
}

export default function SendSmsBroadcastModal() {
  const dispatch = useAppDispatch()
  const { toggleSendSmsBroadcastModal } = modalsSlice.actions
  const visible = useAppSelector((state) => state.modalsReducer.sendSmsBroadcast)

  const [tab, setTab] = useState<'organizers'>('organizers')
  const [audience, setAudience] = useState<OrganizerSmsAudience>('allOrganizers')
  const [textEn, setTextEn] = useState('')
  const [textHe, setTextHe] = useState('')
  const [isTestMode, setIsTestMode] = useState(false)
  const [testPhone, setTestPhone] = useState('+972')
  const [testUserName, setTestUserName] = useState('')
  const [otpOpen, setOtpOpen] = useState(false)
  const [pendingIsTest, setPendingIsTest] = useState(false)

  const usesUserName = useMemo(
    () => textEn.includes('%USER_NAME%') || textHe.includes('%USER_NAME%'),
    [textEn, textHe]
  )

  const [sendOtp, { isLoading: isSendingOtp }] = smsAdminApi.useSendOrganizerSmsOtpMutation()
  const [confirmSend, { isLoading: isConfirming }] = smsAdminApi.useConfirmSendOrganizerSmsMutation()

  const canSend = useMemo(() => Boolean(textEn.trim() || textHe.trim()), [textEn, textHe])

  const resetForm = () => {
    setTab('organizers')
    setAudience('allOrganizers')
    setTextEn('')
    setTextHe('')
    setIsTestMode(false)
    setTestPhone('+972')
    setTestUserName('')
    setOtpOpen(false)
    setPendingIsTest(false)
  }

  const onCloseModal = () => {
    resetForm()
    dispatch(toggleSendSmsBroadcastModal())
  }

  const handleSend = async () => {
    if (!canSend) {
      toast.error(translate('sendSmsValidationTextRequired'))
      return
    }

    let normalizedTestPhone: string | undefined
    if (isTestMode) {
      if (!testPhone || testPhone.length < 8) {
        toast.error(translate('sendSmsTestPhoneRequired'))
        return
      }
      normalizedTestPhone = testPhone.startsWith('+') ? testPhone : `+${testPhone}`
      if (!normalizedTestPhone.startsWith('+972')) {
        toast.error(translate('sendSmsIsraeliOnly'))
        return
      }
      if (usesUserName && !testUserName.trim()) {
        toast.error(translate('sendSmsTestUserNameRequired'))
        return
      }
    }

    const res: any = await sendOtp({
      audience: isTestMode ? undefined : audience,
      testPhone: normalizedTestPhone,
      testUserName: isTestMode ? testUserName.trim() || undefined : undefined,
      textEn: textEn.trim() || undefined,
      textHe: textHe.trim() || undefined,
    })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    setPendingIsTest(isTestMode)
    setOtpOpen(true)
    toast.success(translate('sendSmsOtpSent'))
  }

  const handleResendOtp = async () => {
    let normalizedTestPhone: string | undefined
    if (pendingIsTest) {
      normalizedTestPhone = testPhone.startsWith('+') ? testPhone : `+${testPhone}`
    }
    const res: any = await sendOtp({
      audience: pendingIsTest ? undefined : audience,
      testPhone: normalizedTestPhone,
      testUserName: pendingIsTest ? testUserName.trim() || undefined : undefined,
      textEn: textEn.trim() || undefined,
      textHe: textHe.trim() || undefined,
    })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    toast.success(translate('sendSmsOtpSent'))
  }

  const handleConfirmOtp = async (finalCode: string) => {
    const res: any = await confirmSend({ code: finalCode })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    toast.success(pendingIsTest ? translate('sendSmsTestSuccess') : translate('sendSmsBroadcastStarted'))
    resetForm()
    dispatch(toggleSendSmsBroadcastModal())
  }

  const isBusy = isSendingOtp || isConfirming

  useEffect(() => {
    if (visible !== 'open') return
  }, [visible])

  const renderLangBlock = (lang: 'en' | 'he') => {
    const isEn = lang === 'en'
    const value = isEn ? textEn : textHe
    const setter = isEn ? setTextEn : setTextHe

    return (
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        gap={1}
        p={1.5}
        sx={{
          border: '1px solid #f0e6d8',
          borderRadius: 1,
          backgroundColor: '#fffaf3',
          direction: isEn ? 'ltr' : 'rtl',
        }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ direction: 'ltr' }}>
          {isEn ? 'EN' : 'HE'}
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          color="warning"
          label={isEn ? translate('sendSmsTextEn') : translate('sendSmsTextHe')}
          value={value}
          onChange={(e) => setter(e.target.value)}
          disabled={isBusy}
          multiline
          minRows={4}
          maxRows={10}
          inputProps={{ maxLength: 1000 }}
        />
      </Box>
    )
  }

  return (
    <Modal
      title={translate('sendSmsBroadcastTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '720px' }}
      setVisible={onCloseModal}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          textColor="secondary"
          TabIndicatorProps={{ style: { backgroundColor: '#ED6C02' } }}
          sx={{
            '.MuiTab-root': { color: '#8b8b8b' },
            '.MuiTab-root.Mui-selected': { color: '#ED6C02' },
          }}
        >
          <Tab value="organizers" label={translate('sendSmsTabOrganizers')} />
        </Tabs>

        <Alert severity="info" sx={{ py: 0.5 }}>
          {translate('sendSmsIsraeliOnlyRemark')}
        </Alert>

        <Typography variant="caption" color="textSecondary">
          {translate('sendSmsHelperFreeText')}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {translate('sendSmsHelperUserName')}
        </Typography>

        <Box display="flex" gap={1.5} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          {renderLangBlock('en')}
          {renderLangBlock('he')}
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle2">{translate('sendSmsAudience')}</Typography>
          <RadioGroup value={audience} onChange={(e) => setAudience(e.target.value as OrganizerSmsAudience)}>
            {AUDIENCES.map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                disabled={isBusy || isTestMode}
                control={<Radio color="warning" size="small" />}
                label={translate(`sendSmsAudience_${opt}`)}
              />
            ))}
          </RadioGroup>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              color="warning"
              checked={isTestMode}
              onChange={(e) => setIsTestMode(e.target.checked)}
              disabled={isBusy}
            />
          }
          label={translate('sendSmsTestCheckbox')}
        />

        {isTestMode && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                {translate('sendSmsTestPhoneLabel')}
              </Typography>
              <CustomPhoneInput phone={testPhone} setPhone={setTestPhone} />
            </Box>
            {usesUserName && (
              <TextField
                size="small"
                variant="outlined"
                color="warning"
                label={translate('sendSmsTestUserNameLabel')}
                helperText={translate('sendSmsTestUserNameHelper')}
                value={testUserName}
                onChange={(e) => setTestUserName(e.target.value)}
                disabled={isBusy}
                inputProps={{ maxLength: 200 }}
              />
            )}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            color="warning"
            variant="contained"
            disabled={isBusy || !canSend}
            onClick={handleSend}
          >
            {isSendingOtp ? (
              <CircularProgress size={20} />
            ) : isTestMode ? (
              translate('sendSmsSendTest')
            ) : (
              translate('sendSmsSend')
            )}
          </Button>
        </Box>
      </Box>

      <ConfirmActionOtpModal
        visible={otpOpen}
        title={translate('sendSmsOtpModalTitle')}
        description={translate('sendSmsOtpModalDescription')}
        onClose={() => setOtpOpen(false)}
        onConfirm={handleConfirmOtp}
        onResend={handleResendOtp}
        isConfirming={isConfirming}
        isResending={isSendingOtp}
      />
    </Modal>
  )
}
